import { useEffect, useState } from 'react';
import { apiClient } from '../../../lib/apiClient';
import type { OverviewMetricsSummary } from '../../../types/overviewMetrics';
import type { StationScopeRequest } from '../admin/stationScopeRequest';

interface UseOverviewMetricsArgs {
  // CTV-759: a scope discriminator, not a slug. `{ scope: 'all' }` widens to
  // every station the caller administers, resolved server-side.
  scope: StationScopeRequest;
  startDate: string;
  endDate: string;
}

interface UseOverviewMetricsResult {
  data: OverviewMetricsSummary | null;
  loading: boolean;
  error: string | null;
}

export function useOverviewMetrics({
  scope,
  startDate,
  endDate,
}: UseOverviewMetricsArgs): UseOverviewMetricsResult {
  const [data, setData] = useState<OverviewMetricsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Serialized so a re-render that rebuilds an equal scope object doesn't
  // refire the request.
  const scopeKey = JSON.stringify(scope);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .post<OverviewMetricsSummary>('/overview-metrics/summary', {
        ...(JSON.parse(scopeKey) as StationScopeRequest),
        startDate,
        endDate,
      })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load metrics');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scopeKey, startDate, endDate]);

  return { data, loading, error };
}
